import { useState } from 'react';
import {
  Search,
  Globe,
  FileText,
  Link2,
  Image,
  AlertTriangle,
  CheckCircle,
  Edit2,
  Save,
  X,
  ExternalLink,
  BarChart2,
} from 'lucide-react';
import { cn } from '@/lib/cn';

interface PageSEO {
  id: string;
  path: string;
  title: string;
  description: string;
  keywords: string[];
  ogImage?: string;
  status: 'good' | 'warning' | 'error';
  score: number;
}

// Demo data
const initialPages: PageSEO[] = [
  {
    id: '1',
    path: '/',
    title: 'Bosphorus Labs - Dijital Donusum ve Yazilim Cozumleri',
    description: 'Turkiyenin lider yazilim gelistirme firmasi. Web, mobil uygulama, AI cozumleri ve bulut hizmetleri.',
    keywords: ['yazilim', 'web gelistirme', 'mobil uygulama', 'ai', 'bulut'],
    ogImage: 'https://bosphoruslabs.com/og-image.jpg',
    status: 'good',
    score: 92,
  },
  {
    id: '2',
    path: '/services',
    title: 'Hizmetlerimiz - Bosphorus Labs',
    description: 'Yazilim gelistirme, danismanlik, bulut cozumleri ve AI entegrasyonu hizmetlerimiz.',
    keywords: ['hizmetler', 'yazilim', 'danismanlik'],
    status: 'good',
    score: 88,
  },
  {
    id: '3',
    path: '/products',
    title: 'Urunlerimiz - Bosphorus Labs',
    description: 'Ochtopus IDE, yapay zeka araclari ve isletme yonetim sistemleri.',
    keywords: ['urunler', 'ochtopus', 'ide'],
    status: 'warning',
    score: 72,
  },
  {
    id: '4',
    path: '/about',
    title: 'Hakkimizda',
    description: 'Bosphorus Labs hakkinda bilgi.',
    keywords: ['hakkimizda'],
    status: 'error',
    score: 45,
  },
  {
    id: '5',
    path: '/contact',
    title: 'Iletisim - Bosphorus Labs',
    description: 'Bizimle iletisime gecin. Istanbul Levent ofisimiz.',
    keywords: ['iletisim', 'adres', 'telefon'],
    status: 'good',
    score: 85,
  },
];

const seoTips = [
  { issue: 'Baslik cok kisa', suggestion: 'Baslik 50-60 karakter arasi olmali' },
  { issue: 'Aciklama eksik', suggestion: 'Meta aciklama 150-160 karakter olmali' },
  { issue: 'Anahtar kelime yetersiz', suggestion: 'En az 5 anahtar kelime ekleyin' },
  { issue: 'OG Image eksik', suggestion: 'Sosyal medya paylasimi icin gorsel ekleyin' },
];

export function AdminSEO() {
  const [pages, setPages] = useState<PageSEO[]>(initialPages);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Partial<PageSEO>>({});

  // Filter pages
  const filteredPages = pages.filter(p =>
    p.path.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (page: PageSEO) => {
    setEditingId(page.id);
    setEditValues({ title: page.title, description: page.description, keywords: page.keywords });
  };

  const handleSave = (id: string) => {
    setPages(pages.map(p => p.id === id ? { ...p, ...editValues } : p));
    setEditingId(null);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'good': return <CheckCircle size={16} className="text-green-500" />;
      case 'warning': return <AlertTriangle size={16} className="text-yellow-500" />;
      case 'error': return <AlertTriangle size={16} className="text-red-500" />;
      default: return null;
    }
  };

  const averageScore = Math.round(pages.reduce((acc, p) => acc + p.score, 0) / pages.length);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">SEO Yonetimi</h1>
        <p className="text-text-secondary">Sayfa meta etiketlerini ve SEO ayarlarini duzenleyin</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-accent">{pages.length}</p>
          <p className="text-sm text-text-secondary">Toplam Sayfa</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-500">{pages.filter(p => p.status === 'good').length}</p>
          <p className="text-sm text-text-secondary">Iyi</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-yellow-500">{pages.filter(p => p.status === 'warning').length}</p>
          <p className="text-sm text-text-secondary">Uyari</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <BarChart2 size={24} className="text-accent" />
            <p className="text-2xl font-bold text-text-primary">{averageScore}</p>
          </div>
          <p className="text-sm text-text-secondary">Ort. Skor</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" size={18} />
        <input
          type="text"
          placeholder="Sayfa ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
        />
      </div>

      {/* Pages List */}
      <div className="space-y-4">
        {filteredPages.map(page => (
          <div key={page.id} className="bg-bg-card border border-border-default rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border-default flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getStatusIcon(page.status)}
                <div>
                  <div className="flex items-center gap-2">
                    <code className="text-sm text-accent">{page.path}</code>
                    <a href={page.path} target="_blank" rel="noopener noreferrer" className="text-text-tertiary hover:text-accent">
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className={cn(
                  'px-3 py-1 rounded-full text-sm font-medium',
                  page.score >= 80 ? 'bg-green-500/10 text-green-500' :
                  page.score >= 60 ? 'bg-yellow-500/10 text-yellow-500' :
                  'bg-red-500/10 text-red-500'
                )}>
                  Skor: {page.score}
                </div>
                {editingId !== page.id && (
                  <button
                    onClick={() => handleEdit(page)}
                    className="p-2 rounded-lg hover:bg-bg-elevated text-text-tertiary hover:text-accent"
                  >
                    <Edit2 size={16} />
                  </button>
                )}
              </div>
            </div>

            <div className="p-4 space-y-4">
              {editingId === page.id ? (
                <>
                  <div>
                    <label className="text-xs text-text-tertiary mb-1 block">Baslik ({editValues.title?.length || 0}/60)</label>
                    <input
                      type="text"
                      value={editValues.title || ''}
                      onChange={(e) => setEditValues({ ...editValues, title: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                      maxLength={60}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-text-tertiary mb-1 block">Aciklama ({editValues.description?.length || 0}/160)</label>
                    <textarea
                      value={editValues.description || ''}
                      onChange={(e) => setEditValues({ ...editValues, description: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none resize-none"
                      maxLength={160}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-text-tertiary mb-1 block">Anahtar Kelimeler (virgul ile ayirin)</label>
                    <input
                      type="text"
                      value={editValues.keywords?.join(', ') || ''}
                      onChange={(e) => setEditValues({ ...editValues, keywords: e.target.value.split(',').map(k => k.trim()) })}
                      className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setEditingId(null)}
                      className="px-4 py-2 rounded-lg border border-border-default text-text-secondary hover:text-text-primary"
                    >
                      Iptal
                    </button>
                    <button
                      onClick={() => handleSave(page.id)}
                      className="px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent/90"
                    >
                      Kaydet
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-xs text-text-tertiary mb-1">Baslik</p>
                    <p className="text-text-primary">{page.title}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-tertiary mb-1">Aciklama</p>
                    <p className="text-sm text-text-secondary">{page.description}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-tertiary mb-1">Anahtar Kelimeler</p>
                    <div className="flex flex-wrap gap-1">
                      {page.keywords.map((kw, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-full bg-accent/10 text-accent text-xs">{kw}</span>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* SEO Tips */}
      <div className="bg-bg-card border border-border-default rounded-xl p-6">
        <h2 className="font-semibold text-text-primary mb-4">SEO Ipuclari</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {seoTips.map((tip, i) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-bg-elevated">
              <AlertTriangle size={16} className="text-yellow-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-text-primary">{tip.issue}</p>
                <p className="text-xs text-text-secondary">{tip.suggestion}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
